/**
 * HR estimation from an accumulated BVP/rPPG signal.
 *
 * Uses a power-spectral-density peak in the cardiac band (0.67–3.0 Hz = 40–180 bpm).
 * DFT is evaluated only over the band of interest — fast even for 60s buffers.
 */

const LO_HZ = 0.67;  // 40 bpm
const HI_HZ = 3.0;   // 180 bpm

/** Hanning window coefficient */
const hann = (i: number, n: number) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));

/**
 * Estimate HR in bpm from a BVP/rPPG signal array.
 * @param bvp   Raw BVP samples (arbitrary scale, zero-mean applied internally)
 * @param fps   Sample rate in Hz (default 30)
 * @returns     HR in bpm, or NaN if the signal is too short or has no clear peak
 */
export function hrFromBvp(bvp: number[], fps = 30): number {
  const n = bvp.length;
  if (n < 60) return NaN; // Need at least 2 s

  // Remove mean
  const mean = bvp.reduce((a, b) => a + b, 0) / n;

  // Windowed signal
  const w = bvp.map((x, i) => (x - mean) * hann(i, n));

  // DFT only over the cardiac band
  const kLo = Math.ceil((LO_HZ * n) / fps);
  const kHi = Math.floor((HI_HZ * n) / fps);

  let maxPow = -Infinity;
  let peakHz = 1.0;

  for (let k = kLo; k <= kHi; k++) {
    let re = 0, im = 0;
    const phi = (2 * Math.PI * k) / n;
    for (let t = 0; t < n; t++) {
      re += w[t] * Math.cos(phi * t);
      im -= w[t] * Math.sin(phi * t);
    }
    const pow = re * re + im * im;
    if (pow > maxPow) {
      maxPow = pow;
      peakHz = (k * fps) / n;
    }
  }

  const hr = peakHz * 60;
  return hr >= 40 && hr <= 180 ? hr : NaN;
}

/**
 * Append new BVP samples to a rolling buffer, capped at maxLen.
 * Returns a new array (immutable update).
 */
export function appendBvp(buffer: number[], newSamples: number[], maxLen = 1800): number[] {
  const merged = buffer.concat(newSamples);
  return merged.length > maxLen ? merged.slice(merged.length - maxLen) : merged;
}
